import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const features = [
    {
      icon: 'ri-flashlight-line',
      title: '无需下载',
      description: '打开浏览器即点即玩，无需安装任何客户端或插件，节省时间与设备空间。',
    },
    {
      icon: 'ri-smartphone-line',
      title: '多端适配',
      description: '完美支持PC、平板、手机等多种设备，随时随地畅享游戏乐趣。',
    },
    {
      icon: 'ri-gamepad-line',
      title: '海量游戏',
      description: '200+精品游戏，八大分类，从角色扮演到休闲益智，总有一款适合你。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全可靠',
      description: '平台所有游戏均经过严格审核，保障玩家账号安全，放心畅玩。',
    },
    {
      icon: 'ri-cloud-line',
      title: '云端存档',
      description: '注册账号即可享受云端存档服务，多设备无缝同步游戏进度。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '贴心客服',
      description: '7x24小时在线客服，随时解答你的疑问，提供最优质的服务体验。',
    },
  ];

  const stats = [
    { value: '200+', label: '精品游戏' },
    { value: '500万+', label: '注册玩家' },
    { value: '4.8', label: '用户评分' },
    { value: '99.9%', label: '服务可用率' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      alert('感谢您的留言，我们会尽快回复！');
      (e.target as HTMLFormElement).reset();
      return;
    }
    formData.delete('phone_alt');
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value as string);
    });
    try {
      const resp = await fetch('https://readdy.ai/api/form/d9qu5ffnahb7382ovg1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (resp.ok && parsed?.code === 'OK') {
        alert('感谢您的留言，我们会尽快回复！');
        (e.target as HTMLFormElement).reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        alert(serverMsg.includes('spam') ? '提交失败，请稍后重试。' : serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      alert('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="黄游网页版是专业的在线游戏平台，无需下载安装，直接在浏览器中畅玩。了解我们的品牌故事、核心优势与联系方式。"
        keywords="黄游网页版,关于我们,游戏平台,联系方式"
        canonicalUrl="https://huangyou.com/about"
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 黄游网页版',
          description: '了解黄游网页版的品牌故事、核心优势与联系方式。',
          url: 'https://huangyou.com/about',
        }}
      />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-6 mb-16">
            <h1 className="text-3xl md:text-5xl font-heading text-foreground-900 mb-6">
              关于黄游网页版
            </h1>
            <div className="max-w-[800px]">
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-4">
                <strong>黄游网页版</strong>成立于2023年，是国内领先的在线网页游戏平台。我们致力于为玩家提供<strong>无需下载、即点即玩</strong>的极致游戏体验。
              </p>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-4">
                平台涵盖角色扮演、动作格斗、策略战争、模拟经营、体育竞速、冒险探索、休闲益智、棋牌桌游等<strong>八大游戏分类</strong>，共计超过200款精品游戏，满足各类玩家的需求。
              </p>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                我们相信<strong>好的游戏应该触手可及</strong>。无论你使用PC还是手机，只要能上网，就能在黄游网页版享受流畅、稳定的游戏体验。未来我们将持续引入更多优质游戏，不断优化平台服务，为玩家创造更多快乐。
              </p>
            </div>
          </section>

          <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-background-100 rounded-lg p-5 md:p-6 text-center"
              >
                <p className="text-2xl md:text-4xl font-heading text-primary-600 mb-1">{stat.value}</p>
                <p className="text-xs md:text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </section>

          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-8 text-center">
              为什么选择我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.map((feat) => (
                <div
                  key={feat.title}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className={`${feat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-lg font-heading text-foreground-900 mb-2">{feat.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feat.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-background-100 rounded-lg p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="max-w-[700px] mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">service@huangyou.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-customer-service-2-line text-lg text-accent-700"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">在线客服</h3>
                    <p className="text-sm text-foreground-500">每天24小时在线服务</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-map-pin-line text-lg text-secondary-700"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-500">中国上海市浦东新区张江高科技园区</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-question-answer-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1">商务合作</h3>
                    <p className="text-sm text-foreground-500">business@huangyou.com</p>
                  </div>
                </div>
              </div>

              <form
                id="contact-form"
                data-readdy-form
                className="flex flex-col gap-4"
                onSubmit={handleSubmit}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    placeholder="请输入留言主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  className="form-honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  className="px-8 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer self-start"
                >
                  发送留言
                </button>
              </form>
            </div>
          </section>

          <section className="mt-16 text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">
              准备好开始游戏了吗？
            </h2>
            <p className="text-sm md:text-base text-foreground-500 mb-6">
              加入数百万玩家，在黄游网页版探索属于你的游戏世界
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 text-base font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
              <i className="ri-arrow-right-line"></i>
            </Link>
          </section>
        </div>
      </main>
    </>
  );
}